import { Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface FindingProgressEvent {
  scanRunId: string;
  findingType: string;
  status: string;
  findingsCount?: number;
  durationMs?: number;
  error?: string;
}

export interface ScanFinishedEvent {
  scanRunId: string;
  status: string;
  totalFindings: number;
  finishedAt: string;
}

export interface ScanCancelledEvent {
  scanRunId: string;
  cancelledAt: string;
}

// 🆕 Bulk fix masivo
export interface BulkFixProgressEvent {
  batchId: string;
  itemId: number;
  status: string;
  affectedRows?: number;
  error?: string;
}

export interface BulkFixFinishedEvent {
  batchId: string;
  status: string;
  successCount: number;
  failedCount: number;
  skippedCount: number;
}

export interface BulkFixCancelledEvent {
  batchId: string;
  cancelledAt: string;
}

@Injectable({ providedIn: 'root' })
export class AuditHubService implements OnDestroy {
  private connection?: signalR.HubConnection;
  private currentSubscribedScan?: string;
  private currentSubscribedBulkFix?: string;

  readonly findingProgress$ = new Subject<FindingProgressEvent>();
  readonly scanFinished$ = new Subject<ScanFinishedEvent>();
  readonly scanCancelled$ = new Subject<ScanCancelledEvent>();
  readonly bulkFixProgress$ = new Subject<BulkFixProgressEvent>();
  readonly bulkFixFinished$ = new Subject<BulkFixFinishedEvent>();
  readonly bulkFixCancelled$ = new Subject<BulkFixCancelledEvent>();
  readonly connectionState$ = new Subject<'connected' | 'disconnected'>();

  constructor(private authService: AuthService) {}

  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) return;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.signalRUrl}/hubs/audit`, {
        accessTokenFactory: () => this.authService.getAccessToken() || ''
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .build();

    this.connection.on('FindingProgress', (e: FindingProgressEvent) =>
      this.findingProgress$.next(e));
    this.connection.on('ScanFinished', (e: ScanFinishedEvent) =>
      this.scanFinished$.next(e));
    this.connection.on('ScanCancelled', (e: ScanCancelledEvent) =>
      this.scanCancelled$.next(e));
    this.connection.on('BulkFixProgress', (e: BulkFixProgressEvent) =>
      this.bulkFixProgress$.next(e));
    this.connection.on('BulkFixFinished', (e: BulkFixFinishedEvent) =>
      this.bulkFixFinished$.next(e));
    this.connection.on('BulkFixCancelled', (e: BulkFixCancelledEvent) =>
      this.bulkFixCancelled$.next(e));

    this.connection.onreconnected(() => {
      this.connectionState$.next('connected');
      if (this.currentSubscribedScan) {
        this.connection!.invoke('SubscribeToScan', this.currentSubscribedScan);
      }
      if (this.currentSubscribedBulkFix) {
        this.connection!.invoke('SubscribeToBulkFix', this.currentSubscribedBulkFix);
      }
    });
    this.connection.onclose(() => this.connectionState$.next('disconnected'));

    await this.connection.start();
    this.connectionState$.next('connected');
  }

  async subscribeToScan(scanRunId: string): Promise<void> {
    if (!this.connection) await this.connect();
    this.currentSubscribedScan = scanRunId;
    await this.connection!.invoke('SubscribeToScan', scanRunId);
  }

  async unsubscribeFromScan(scanRunId: string): Promise<void> {
    if (!this.connection) return;
    await this.connection.invoke('UnsubscribeFromScan', scanRunId);
    this.currentSubscribedScan = undefined;
  }

  async subscribeToBulkFix(batchId: string): Promise<void> {
    if (!this.connection) await this.connect();
    this.currentSubscribedBulkFix = batchId;
    await this.connection!.invoke('SubscribeToBulkFix', batchId);
  }

  async unsubscribeFromBulkFix(batchId: string): Promise<void> {
    if (!this.connection) return;
    await this.connection.invoke('UnsubscribeFromBulkFix', batchId);
    this.currentSubscribedBulkFix = undefined;
  }

  ngOnDestroy(): void {
    this.connection?.stop();
  }
}
